import api from "api";
import { push } from "connected-react-router";
import { delay } from "redux-saga";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import fetchInitialData from "../../shared/sagas";
import { policies, resources, services } from "../actions";
import {
  ADD_SERVICE_WITH_ADMIN_START,
  FETCH_SERVICES_START,
  FETCH_SERVICES_SUCCESS,
  REGISTER_SERVICE_START,
  REMOVE_SERVICE_START
} from "../constants";
import selectors from "../selectors";
import { addGroupSaga, fetchGroupByNameSaga } from "./groups";
import { addPolicySaga } from "./policies";
import { addUserSaga, fetchUserByUsernameSaga } from "./users";
import { fetchPagedSagaFactory, REFRESH_DELAY } from "./utils";

export const servicesSaga = fetchPagedSagaFactory(
  api.Services.get,
  services.success,
  services.failed
);

export function* preselectServiceSaga() {
  // Check whether non-RBAC services exist, then preselect the first service
  const filteredServices = yield select(selectors.services.getFiltered);
  if (filteredServices.length) {
    yield put(services.setCurrent(filteredServices[0].$uri));
  } else {
    yield put(push("/noservices"));
  }
}

export function* addServiceSaga(service) {
  try {
    const { response } = yield call(api.Services.create, service);
    yield put(services.addSuccess(response));
    return response;
  } catch (error) {
    yield put(services.addFailed(error));
    throw error;
  }
}

export function* removeServiceSaga(action) {
  try {
    const { service } = action;

    yield call(api.Services.delete, service);

    // Ensure server state has updated
    yield call(delay, REFRESH_DELAY);

    // Remove resources and policies associated with service from state
    const resourcesToRemove = yield select(
      selectors.resources.getByCurrentService
    );
    const policiesToRemove = yield select(
      selectors.policies.getAllByCurrentService
    );
    yield all(
      resourcesToRemove.map(resource => put(resources.remove(resource)))
    );
    yield put(policies.commitDeleteSuccess(policiesToRemove));
    yield put(services.removeSuccess(service));
    yield put(services.request());
    yield put(resources.request());
    yield put(policies.request());
  } catch (error) {
    yield put(services.removeFailed(error));
  }
}

export function* registerServiceSaga(action) {
  try {
    const { service, keyPair } = action;
    const { response } = yield call(
      api.Services.register,
      service,
      keyPair.public
    );
    yield put(services.registerSuccess(service, response));
  } catch (error) {
    yield put(services.registerFailed(error));
  }
}

export function* addServiceModalUserSaga(service, adminRole, username) {
  const userQueryResult = yield call(fetchUserByUsernameSaga, username);

  let userResult;
  if (userQueryResult.length > 0) {
    [userResult] = userQueryResult;
  } else {
    // create user if does not already exist
    userResult = yield call(addUserSaga, username);
  }

  // create rbac admin policy
  const rbacPolicy = {
    service: { $ref: service.$uri },
    user: { $ref: userResult.$uri },
    role: { $ref: adminRole.$uri }
  };
  // create admin policies for all resources for this service
  yield put(services.setCurrent(service.$uri));
  const serviceResources = yield select(
    selectors.resources.getByCurrentService
  );
  const servicePolicies = serviceResources.map(resource => ({
    resource: { $ref: resource.$uri },
    user: { $ref: userResult.$uri },
    role: { $ref: adminRole.$uri }
  }));
  const policies = [rbacPolicy, ...servicePolicies];
  return policies;
}

export function* addServiceModalGroupSaga(service, adminRole, groupname) {
  const groupQueryResult = yield call(fetchGroupByNameSaga, groupname);

  let groupResult;
  if (groupQueryResult.length > 0) {
    [groupResult] = groupQueryResult;
  } else {
    // create group if does not already exist
    groupResult = yield call(addGroupSaga, groupname);
  }

  // create rbac admin policy
  const rbacPolicy = {
    service: { $ref: service.$uri },
    group: { $ref: groupResult.$uri },
    role: { $ref: adminRole.$uri }
  };
  // create admin policies for all resources for this service
  yield put(services.setCurrent(service.$uri));
  const serviceResources = yield select(
    selectors.resources.getByCurrentService
  );
  const servicePolicies = serviceResources.map(resource => ({
    resource: { $ref: resource.$uri },
    group: { $ref: groupResult.$uri },
    role: { $ref: adminRole.$uri }
  }));
  const policies = [rbacPolicy, ...servicePolicies];
  return policies;
}

export function* addServiceModalSaga(action) {
  try {
    const { service, username, groupname } = action;
    const serviceResult = yield call(addServiceSaga, service);

    if (username || groupname) {
      // ensure new resources are available.
      yield call(delay, REFRESH_DELAY);
      yield all([put(resources.request()), put(services.request())]);

      const adminRole = yield select(selectors.roles.getAdmin);

      let policies;
      if (username) {
        policies = yield addServiceModalUserSaga(
          serviceResult,
          adminRole,
          username
        );
      } else if (groupname) {
        policies = yield addServiceModalGroupSaga(
          serviceResult,
          adminRole,
          groupname
        );
      }
      yield all(policies.map(p => call(addPolicySaga, p)));
    }

    const errors = yield select(selectors.services.hasErrors);
    if (!errors) {
      yield put(push("/policies"));
      yield put(services.addWithAdminSuccess());
      // Ensure server state has updated,
      // before refreshing.
      yield call(delay, REFRESH_DELAY);
      // refresh data
      yield call(fetchInitialData);
    }
  } catch (error) {
    yield put(services.addWithAdminFailed(error));
  }
}

export function* watchServices() {
  yield takeLatest(FETCH_SERVICES_START, servicesSaga);
}

export function* watchPreselectService() {
  yield takeLatest(FETCH_SERVICES_SUCCESS, preselectServiceSaga);
}

export function* watchAddService() {
  yield takeLatest(ADD_SERVICE_WITH_ADMIN_START, addServiceModalSaga);
}

export function* watchRemoveService() {
  yield takeLatest(REMOVE_SERVICE_START, removeServiceSaga);
}

export function* watchRegisterService() {
  yield takeLatest(REGISTER_SERVICE_START, registerServiceSaga);
}
